import { createApi } from "@reduxjs/toolkit/query/react";
import { apiFunction } from "../utils";
import { baseQueryWithReauth } from "../baseQueryWithReauth";
import { serializeArgsWithoutToken } from "../serializeQueryArgs";

const storageDevicesApi = createApi({
  reducerPath: "storageDevices",
  baseQuery: baseQueryWithReauth,
  serializeQueryArgs: serializeArgsWithoutToken, // #142: token önbellek anahtarına girmez
  keepUnusedDataFor: 120,
  endpoints(builder) {
    return {
      toggleDeviceActivity: builder.mutation({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/toggleDeviceActivity", "PATCH");
        },
      }),
      moveDeviceToFirm: builder.mutation({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/moveDeviceToFirm", "PATCH");
        },
      }),
      moveDeviceToPlaymaker: builder.mutation({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/moveDeviceToPlaymaker", "PATCH");
        },
      }),
      moveDeviceToService: builder.mutation({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/moveDeviceToService", "PATCH");
        },
      }),
      moveDeviceToStorage: builder.mutation({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/moveDeviceToStorage", "PATCH");
        },
      }),
      moveDeviceToProduction: builder.mutation({
        query: (data) => {
          return apiFunction(
            data,
            "/api/devices/storageRoles/moveDeviceToProduction",
            "PATCH"
          );
        },
      }),
      // Merkezden bayi adına taşıma (#329, sözleşme dealer-device-moves.md v1.1)
      // Düğmeye basınca: bayinin müşterileri / müşterinin bayileri
      getDealerMoveOptions: builder.mutation({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/dealerMoveOptions", "POST");
        },
      }),
      moveDeviceToDealerCustomer: builder.mutation({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/moveDeviceToDealerCustomer", "PATCH");
        },
      }),
      takeBackDeviceToDealer: builder.mutation({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/takeBackDeviceToDealer", "PATCH");
        },
      }),
      // Bayi cihazına Kota Ver / Kota Al (#335, sözleşme dealer-device-moves.md v1.2)
      getDealerDeviceQuotaOptions: builder.mutation({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/dealerDeviceQuotaOptions", "POST");
        },
      }),
      giveDealerDeviceQuota: builder.mutation({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/giveDealerDeviceQuota", "PATCH");
        },
      }),
      takeBackDealerDeviceQuota: builder.mutation({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/takeBackDealerDeviceQuota", "PATCH");
        },
      }),
      resetOperationQuota: builder.mutation({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/resetOperationQuota", "PATCH");
        },
      }),
      createEspressoDevice: builder.mutation({
        query: (data) => {
          return apiFunction(data, "/api/devices/storageRoles/createEspressoDevice", "POST");
        },
      }),
      // Espresso desteklenen komut listesi (Kalerm 2.12). Tab açılışında çağrılır.
      // data: { token, serialNo }
      getRemoteControlCommands: builder.query({
        query: (data) => {
          return apiFunction(
            data,
            "/api/devices/storageRoles/remoteControl/commands",
            "POST"
          );
        },
        transformResponse: (response) => response?.data ?? response,
      }),
      // Espresso uzaktan kontrol komutu (Kalerm/Coffee Cloud 2.13).
      // data: { token, serialNo, command }
      sendRemoteControlCommand: builder.mutation({
        query: (data) => {
          return apiFunction(
            data,
            "/api/devices/storageRoles/remoteControl/send",
            "POST"
          );
        },
      }),
      // Cihaz developer parolasini admin'e goster. Server controlPassword
      // middleware'i kaldirildi (panel JWT + admin role yeterli); body'de
      // password/code yok. Audit log server tarafinda devam ediyor.
      getDeveloperPassword: builder.mutation({
        query: ({ token, deviceId }) => {
          return apiFunction(
            { token },
            `/api/devices/storageRoles/${deviceId}/developer-password`,
            "POST"
          );
        },
      }),
      // Admin "Creds Yenile" — cihazin per-device secret'larini rotate edip
      // MQTT ile cihaza gonderir. Hassas islem: parola + 2FA (deleteDevice
      // ile ayni). Cihaz uygulayinca socket "device:credsRefreshAck" gelir.
      refreshDeviceCreds: builder.mutation({
        query: ({ token, id, password, passwordConfirm, code }) => {
          return apiFunction(
            { token, id, password, passwordConfirm, code },
            "/api/devices/storageRoles/refreshDeviceCreds",
            "POST"
          );
        },
      }),
    };
  },
});

export const {
  useToggleDeviceActivityMutation,
  useMoveDeviceToFirmMutation,
  useMoveDeviceToPlaymakerMutation,
  useMoveDeviceToServiceMutation,
  useMoveDeviceToStorageMutation,
  useMoveDeviceToProductionMutation,
  useGetDealerMoveOptionsMutation,
  useMoveDeviceToDealerCustomerMutation,
  useTakeBackDeviceToDealerMutation,
  useGetDealerDeviceQuotaOptionsMutation,
  useGiveDealerDeviceQuotaMutation,
  useTakeBackDealerDeviceQuotaMutation,
  useResetOperationQuotaMutation,
  useCreateEspressoDeviceMutation,
  useGetRemoteControlCommandsQuery,
  useSendRemoteControlCommandMutation,
  useGetDeveloperPasswordMutation,
  useRefreshDeviceCredsMutation,
} = storageDevicesApi;
export { storageDevicesApi };
